import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { cmsApi, AppConfig } from '../services/cmsApi';

interface AppConfigContextType {
  config: AppConfig | null;
  loading: boolean;
  error: boolean; // true = không gọi được BE → gate hiển thị trang bảo trì
  refresh: () => void;
}

const AppConfigContext = createContext<AppConfigContextType>({
  config: null,
  loading: true,
  error: false,
  refresh: () => {},
});

/**
 * Nạp /api/config MỖI lần load site (không cache) để luôn có dataVersion mới nhất.
 * Config là "canary" kiểm tra Backend: fetch fail → error=true → site public hiện Maintenance.
 * KHÔNG có fallback.
 */
export function AppConfigProvider({ children }: { children: React.ReactNode }) {
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const startedRef = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const cfg = await cmsApi.getConfig();
      if (!cfg) throw new Error('config rỗng');
      setConfig(cfg);
    } catch (err) {
      console.error('[AppConfig] không tải được config:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (startedRef.current) return; // chặn double-fetch (React 18 StrictMode)
    startedRef.current = true;
    void load();
  }, [load]);

  const refresh = useCallback(() => {
    void load();
  }, [load]);

  return (
    <AppConfigContext.Provider value={{ config, loading, error, refresh }}>
      {children}
    </AppConfigContext.Provider>
  );
}

export function useAppConfig() {
  return useContext(AppConfigContext);
}
